import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { addToast, toggleTheme } from '../../store/slices/uiSlice';
import { logout, clearCredentials } from '../../store/slices/authSlice';
import { buildAvatarUrl } from '../../utils/imageUrl';
import { useUnreadMessages } from '../../hooks/useUnreadMessages';
import useInstalledAppView from '../../hooks/useInstalledAppView';
import useNativeBiometricLock from '../../hooks/useNativeBiometricLock';
import { LanguageSelector } from '../common';
import {
  Home,
  Gavel,
  Video,
  User,
  X,
  MessageCircle,
  Users,
  TrendingUp,
  Wallet,
  Settings,
  Trophy,
  ArrowLeftRight,
  Radio,
  LayoutDashboard,
  UserPlus,
  Star,
  CreditCard,
  Lock,
  BarChart3,
  Archive,
  Search,
  Megaphone,
  Briefcase,
  Sparkles,
  HelpCircle,
  Ticket,
  Moon,
  Sun,
  Bell,
  ShieldCheck,
  UserX,
  FileText,
  LifeBuoy,
  RefreshCw,
  RotateCcw,
  LogOut,
  Headphones,
  Fingerprint,
} from 'lucide-react';

const activeNavClass = 'text-emerald-600 dark:text-emerald-300';
const inactiveNavClass = 'text-gray-500 dark:text-[#d8e6f8]';
const inactiveSheetItemClass = 'bg-gray-50 dark:bg-[#131a45] text-gray-600 dark:text-[#d8e6f8] border border-gray-100 dark:border-[#34447e] hover:border-emerald-300 dark:hover:border-emerald-300/60';

const MobileBottomNav: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const { balance } = useAppSelector((state) => state.wallet);
  const { theme } = useAppSelector((state) => state.ui);
  const { creatorApplied, creatorStatus, managerApplied, managerStatus } = useAppSelector(
    (state) => state.applicationStatus
  );
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [appAction, setAppAction] = useState<'refresh' | 'cache' | null>(null);
  const isInstalledAppView = useInstalledAppView();
  const biometricLock = useNativeBiometricLock(isInstalledAppView);
  const unreadCount = useUnreadMessages();

  if (!isAuthenticated) return null;

  const mainNavItems = [
    { to: '/feed', label: 'Feed', icon: Home },
    { to: '/streams', label: 'Live', icon: Video, badge: true },
    { to: '/game-a-rama', label: 'Game-a-Rama', icon: Gavel },
    { to: '/messages', label: 'Chat', icon: MessageCircle },
  ];

  const isPlainUser = user?.role === 'user';
  const showCreatorBtn = !creatorApplied && !(managerApplied && managerStatus !== 'rejected');
  const showManagerBtn = !managerApplied && !(creatorApplied && creatorStatus !== 'rejected');

  const moreGroups = [
    {
      label: 'Explore',
      items: [
        { to: '/games', label: 'Games', icon: Trophy },
        { to: '/otc', label: 'Token Exchange', icon: ArrowLeftRight },
        { to: '/users', label: 'Discover', icon: Users },
        { to: '/trending', label: 'Trending', icon: TrendingUp },
      ],
    },
    {
      label: 'My Account',
      items: [
        { to: '/profile', label: 'Profile', icon: User },
        { to: '/dashboard', label: 'Dashboard', icon: Settings },
        { to: '/wallet', label: 'Wallet', icon: Wallet },
        { to: '/subscriptions', label: 'Subscriptions', icon: Star },
        { to: '/game-a-rama/my-wins', label: 'My Wins', icon: Trophy },
        { to: '/ads', label: 'Advertise', icon: Megaphone },
      ],
    },
    ...(user?.role === 'model' ? [{
      label: 'Creator Tools',
      accent: 'text-pink-400',
      items: [
        { to: '/go-live', label: 'Go Live', icon: Radio, highlight: true },
        { to: '/streaming/archives', label: 'Archived Streams', icon: Archive },
        { to: '/subscriptions/manage', label: 'Plans', icon: CreditCard },
        { to: '/subscriptions/subscribers', label: 'Subscribers', icon: Users },
        { to: '/subscriptions/earnings', label: 'Earnings', icon: BarChart3 },
        { to: '/posts/locked', label: 'Locked Posts', icon: Lock },
      ],
    }] : []),
    ...(user?.role === 'manager' ? [{
      label: 'Manager Tools',
      accent: 'text-emerald-400',
      items: [
        { to: '/manager/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { to: '/manager/creators/new', label: 'Add Creator', icon: UserPlus },
      ],
    }] : []),
    ...(isPlainUser && (showCreatorBtn || showManagerBtn) ? [{
      label: 'Choose Your Role',
      accent: 'text-teal-400',
      items: [
        ...(showCreatorBtn ? [{ to: '/profile', label: 'Be a Creator', icon: Sparkles, highlight: true }] : []),
        ...(showManagerBtn ? [{ to: '/profile', label: 'Be a Manager', icon: Briefcase, highlight: true }] : []),
      ],
    }] : []),
    {
      label: 'Help & Support',
      items: [
        { to: '/help', label: 'Help Center', icon: HelpCircle },
        { to: '/faq', label: 'FAQ', icon: MessageCircle },
        { to: '/contact', label: 'Contact Us', icon: Settings },
        { to: '/support/tickets', label: 'My Tickets', icon: Ticket },
      ],
    },
  ];

  const appSettingLinks = [
    { to: '/profile', label: 'Account', icon: User },
    { to: '/notifications', label: 'Alerts', icon: Bell },
    { to: '/wallet', label: 'Wallet', icon: Wallet },
    { to: '/blocked-users', label: 'Blocked', icon: UserX },
    { to: '/privacy', label: 'Privacy', icon: FileText },
    { to: '/safety', label: 'Safety', icon: ShieldCheck },
    { to: '/help', label: 'Help', icon: LifeBuoy },
    { to: '/support/tickets', label: 'Tickets', icon: Ticket },
  ];

  const isActive = (path: string) => {
    if (path === '/feed') return location.pathname === '/feed' || location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
    dispatch(addToast({
      type: 'success',
      message: `Switched to ${theme === 'light' ? 'dark' : 'light'} mode`,
    }));
  };

  const handleRefreshApp = () => {
    setAppAction('refresh');
    setShowMoreMenu(false);
    dispatch(addToast({ type: 'info', message: 'Refreshing app...' }));
    window.setTimeout(() => window.location.reload(), 500);
  };

  const handleOpenSupportChat = () => {
    setShowMoreMenu(false);
    window.setTimeout(() => {
      window.dispatchEvent(new Event('bid4babes:open-support-chat'));
    }, 120);
    dispatch(addToast({ type: 'info', message: 'Opening support chat...' }));
  };

  const handleRefreshAppCache = async () => {
    setAppAction('cache');
    setShowMoreMenu(false);
    dispatch(addToast({ type: 'info', message: 'Clearing app cache...' }));
    try {
      if ('caches' in window) {
        const cacheNames = await window.caches.keys();
        await Promise.all(cacheNames.map((cacheName) => window.caches.delete(cacheName)));
      }

      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.update().catch(() => undefined)));
      }

      dispatch(addToast({ type: 'success', message: 'App cache cleared. Reloading...' }));
    } finally {
      window.setTimeout(() => window.location.reload(), 650);
    }
  };

  const handleBiometricLockToggle = () => {
    if (!biometricLock.available) {
      dispatch(addToast({
        type: 'error',
        message: biometricLock.message || 'Set up Face ID, fingerprint, or device lock on this phone first.',
      }));
      biometricLock.refresh();
      return;
    }

    const nextEnabled = !biometricLock.enabled;
    const requested = biometricLock.setEnabled(nextEnabled);
    if (!requested) {
      dispatch(addToast({ type: 'error', message: 'App lock is only available in the iOS or Android app.' }));
      return;
    }

    dispatch(addToast({
      type: 'info',
      message: nextEnabled
        ? 'Confirm with your phone security to turn on app lock.'
        : 'Confirm with your phone security to turn off app lock.',
    }));
  };

  const handleLogout = async () => {
    localStorage.removeItem('managerOriginalTokens');
    setShowMoreMenu(false);

    try {
      await dispatch(logout()).unwrap();
    } catch {
      dispatch(clearCredentials());
    } finally {
      navigate('/login', { replace: true });
    }
  };

  return (
    <>
      <AnimatePresence>
        {showMoreMenu && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              onClick={() => setShowMoreMenu(false)}
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-[#0d1333] rounded-t-3xl shadow-2xl border-t border-gray-200 dark:border-[#1e2a5e] max-h-[82vh] overflow-y-auto"
            >
              <div className="flex justify-center pt-3 pb-1 sticky top-0 bg-white dark:bg-[#0d1333] z-10">
                <div className="w-10 h-1 bg-gray-300 dark:bg-[#2a3a7e] rounded-full" />
              </div>
              <div className="px-5 py-4 border-b border-gray-100 dark:border-[#1e2a5e] sticky top-5 bg-white dark:bg-[#0d1333] z-10">
                <div className="flex items-center gap-3">
                  <img
                    src={buildAvatarUrl(user?.avatar_url) || '/default-avatar.png'}
                    alt={user?.username}
                    className="w-11 h-11 rounded-full border-2 border-emerald-500 object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 dark:text-white truncate text-sm">{user?.username}</p>
                    <p className="text-xs text-emerald-500 font-bold">{balance.toLocaleString()} tokens</p>
                  </div>
                  <button
                    onClick={() => setShowMoreMenu(false)}
                    className="p-2 rounded-xl bg-gray-100 dark:bg-[#131a45]"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-[#d8e6f8]" />
                  </button>
                </div>
              </div>
              <div className="p-4 space-y-6 pb-8">
                {isInstalledAppView && (
                  <div className="rounded-2xl border border-gray-100 dark:border-[#34447e] bg-gray-50 dark:bg-[#10173d] p-4 shadow-sm">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-[#9fb5d6] mb-3 px-1">
                      App Settings
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">Theme</p>
                          <p className="text-xs text-gray-500 dark:text-[#9fb5d6]">Switch light or dark mode</p>
                        </div>
                        <button
                          onClick={handleThemeToggle}
                          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-bold text-gray-700 shadow-sm transition-all hover:border-emerald-300 hover:text-emerald-600 dark:border-[#34447e] dark:bg-[#131a45] dark:text-[#d8e6f8] dark:hover:border-emerald-300/60 dark:hover:text-emerald-300"
                          aria-label="Toggle theme"
                        >
                          {theme === 'light' ? (
                            <Moon className="w-4 h-4" />
                          ) : (
                            <Sun className="w-4 h-4 text-yellow-300" />
                          )}
                          <span>{theme === 'light' ? 'Dark' : 'Light'}</span>
                        </button>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white">Language</p>
                          <p className="text-xs text-gray-500 dark:text-[#9fb5d6]">Change app language</p>
                        </div>
                        <div className="flex-shrink-0">
                          <LanguageSelector />
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-2 pt-1">
                        {appSettingLinks.map((item) => {
                          const Icon = item.icon;
                          return (
                            <Link
                              key={item.label}
                              to={item.to}
                              onClick={() => setShowMoreMenu(false)}
                              className="flex min-h-[74px] flex-col items-center justify-center gap-1.5 rounded-2xl border border-gray-100 bg-white px-2 py-3 text-center text-gray-600 shadow-sm transition-all hover:border-emerald-300 hover:text-emerald-600 dark:border-[#34447e] dark:bg-[#131a45] dark:text-[#d8e6f8] dark:hover:border-emerald-300/60 dark:hover:text-emerald-300"
                            >
                              <Icon className="h-5 w-5" />
                              <span className="text-[10px] font-bold leading-tight">{item.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <button
                          type="button"
                          onClick={handleBiometricLockToggle}
                          disabled={biometricLock.pending}
                          className={`flex min-h-[64px] flex-col items-center justify-center gap-1.5 rounded-2xl border px-2 py-3 text-center shadow-sm transition-all ${
                            biometricLock.enabled
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 dark:border-emerald-300/60 dark:bg-emerald-500/15 dark:text-emerald-200'
                              : 'border-gray-100 bg-white text-gray-600 hover:border-emerald-300 hover:text-emerald-600 dark:border-[#34447e] dark:bg-[#131a45] dark:text-[#d8e6f8] dark:hover:border-emerald-300/60 dark:hover:text-emerald-300'
                          }`}
                          aria-pressed={biometricLock.enabled}
                          title={biometricLock.label}
                        >
                          <Fingerprint className={`h-5 w-5 ${biometricLock.pending ? 'animate-pulse' : ''}`} />
                          <span className="text-[10px] font-bold leading-tight">
                            {biometricLock.enabled ? 'Lock On' : 'App Lock'}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={handleOpenSupportChat}
                          className="flex min-h-[64px] flex-col items-center justify-center gap-1.5 rounded-2xl border border-gray-100 bg-white px-2 py-3 text-center text-gray-600 shadow-sm transition-all hover:border-emerald-300 hover:text-emerald-600 dark:border-[#34447e] dark:bg-[#131a45] dark:text-[#d8e6f8] dark:hover:border-emerald-300/60 dark:hover:text-emerald-300"
                        >
                          <Headphones className="h-5 w-5" />
                          <span className="text-[10px] font-bold leading-tight">Support</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleRefreshApp}
                          disabled={appAction !== null}
                          className="flex min-h-[64px] flex-col items-center justify-center gap-1.5 rounded-2xl border border-gray-100 bg-white px-2 py-3 text-center text-gray-600 shadow-sm transition-all hover:border-emerald-300 hover:text-emerald-600 dark:border-[#34447e] dark:bg-[#131a45] dark:text-[#d8e6f8] dark:hover:border-emerald-300/60 dark:hover:text-emerald-300"
                        >
                          <RefreshCw className={`h-5 w-5 ${appAction === 'refresh' ? 'animate-spin' : ''}`} />
                          <span className="text-[10px] font-bold leading-tight">{appAction === 'refresh' ? 'Refreshing' : 'Refresh'}</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleRefreshAppCache}
                          disabled={appAction !== null}
                          className="flex min-h-[64px] flex-col items-center justify-center gap-1.5 rounded-2xl border border-gray-100 bg-white px-2 py-3 text-center text-gray-600 shadow-sm transition-all hover:border-emerald-300 hover:text-emerald-600 dark:border-[#34447e] dark:bg-[#131a45] dark:text-[#d8e6f8] dark:hover:border-emerald-300/60 dark:hover:text-emerald-300"
                        >
                          <RotateCcw className={`h-5 w-5 ${appAction === 'cache' ? 'animate-spin' : ''}`} />
                          <span className="text-[10px] font-bold leading-tight">{appAction === 'cache' ? 'Clearing' : 'Cache'}</span>
                        </button>
                        <LanguageSelector variant="appTile" />
                      </div>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex w-full items-center justify-center gap-2 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-600 transition-all hover:border-red-200 hover:bg-red-100 dark:border-red-500/25 dark:bg-red-500/10 dark:text-red-200 dark:hover:border-red-400/50 dark:hover:bg-red-500/15"
                      >
                        <LogOut className="h-4 w-4" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
                {moreGroups.map((group) => (
                  <div key={group.label}>
                    <p className={`text-[10px] font-bold uppercase tracking-widest mb-3 px-1 ${'accent' in group && group.accent ? group.accent : 'text-gray-400 dark:text-[#9fb5d6]'}`}>
                      {group.label}
                    </p>
                    <div className="grid grid-cols-3 gap-2.5">
                      {group.items.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.to);
                        const isHighlight = 'highlight' in item && item.highlight;
                        return (
                          <Link
                            key={item.label}
                            to={item.to}
                            onClick={() => setShowMoreMenu(false)}
                            className={`flex flex-col items-center gap-2 p-4 rounded-2xl transition-all text-center ${isHighlight
                              ? 'bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg shadow-emerald-500/20'
                              : active
                                ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-300/50 shadow-sm dark:shadow-emerald-950/30'
                                : inactiveSheetItemClass
                              }`}
                          >
                            <Icon className="w-6 h-6" />
                            <span className="text-[11px] font-semibold leading-tight">{item.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}

              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-[#0d1333]/95 backdrop-blur-xl border-t border-gray-200 dark:border-[#34447e] shadow-2xl dark:shadow-[0_-18px_40px_rgba(2,6,23,0.55)] safe-area-pb">
        <div className="flex items-center justify-around px-1 py-1">
          {mainNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            const isMessages = item.to === '/messages';
            const hasUnread = isMessages && unreadCount > 0;
            return (
              <Link key={item.to} to={item.to} className="flex-1 min-w-0">
                <motion.div
                  whileTap={{ scale: 0.88 }}
                  className={`flex flex-col items-center gap-0.5 py-2 px-2 rounded-xl transition-all ${active ? activeNavClass : inactiveNavClass
                    }`}
                >
                  <div className="relative">
                    <motion.div
                      animate={hasUnread && !active ? { rotate: [0, -12, 12, -8, 8, 0] } : {}}
                      transition={{ repeat: Infinity, repeatDelay: 4, duration: 0.5 }}
                    >
                      <Icon className={`w-5 h-5 transition-transform ${active ? 'scale-110' : ''} ${hasUnread ? 'text-emerald-600 dark:text-emerald-300' : ''}`} />
                    </motion.div>
                    {item.badge && !isMessages && (
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    )}
                    {hasUnread && (
                      <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 bg-emerald-500 text-white rounded-full flex items-center justify-center text-[9px] font-bold px-0.5 animate-pulse shadow-lg shadow-emerald-500/40">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </div>
                  <span className={`text-[10px] font-semibold truncate ${active || hasUnread ? activeNavClass : ''}`}>
                    {item.label}
                  </span>
                </motion.div>
              </Link>
            );
          })}

          <Link to="/search" className="flex-1 min-w-0">
            <motion.div
              whileTap={{ scale: 0.88 }}
              className={`flex flex-col items-center gap-0.5 py-2 px-2 rounded-xl transition-all ${isActive('/search') ? activeNavClass : inactiveNavClass}`}
            >
              <Search className={`w-5 h-5 transition-transform ${isActive('/search') ? 'scale-110' : ''}`} />
              <span className={`text-[10px] font-semibold ${isActive('/search') ? activeNavClass : ''}`}>Search</span>
            </motion.div>
          </Link>

          <button onClick={() => setShowMoreMenu(true)} className="flex-1 min-w-0">
            <motion.div
              whileTap={{ scale: 0.88 }}
              className={`flex flex-col items-center gap-0.5 py-2 px-2 rounded-xl transition-all ${showMoreMenu ? activeNavClass : inactiveNavClass
                }`}
            >
              <div className="relative">
                <img
                  src={buildAvatarUrl(user?.avatar_url) || '/default-avatar.png'}
                  alt={user?.username}
                  className={`w-6 h-6 rounded-full object-cover border-2 transition-all ${showMoreMenu ? 'border-emerald-500 dark:border-emerald-300 scale-110' : 'border-gray-300 dark:border-[#d8e6f8]'
                    }`}
                />
              </div>
              <span className={`text-[10px] font-semibold ${showMoreMenu ? activeNavClass : ''}`}>More</span>
            </motion.div>
          </button>
        </div>
      </nav>
    </>
  );
};

export default MobileBottomNav;
